<script lang="ts">
	import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
	import AlignJustifyIcon from "@lucide/svelte/icons/align-justify";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";

	import { Footer, FooterColumn, FooterLink } from "#lib/web-ui/fergmar-com/index.js";
	import { FooterWrapper, PodcastPlayer } from "#lib/web-ui/blocks/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Toaster } from "svelte-sonner";
	import { FaviconMeta } from "#lib/web-ui/meta/index.js";
	import { PUBLIC_NODE_ENV } from "$app/env/public";
	import { cloudinaryUrl } from "#lib/shared/cloudinary.js";
	import { page } from "$app/state";
	import { partytownSnippet } from "@qwik.dev/partytown/integration";
	import { browser } from "$app/env";
	import type { LayoutProps } from "./$types";

	let { children, data }: LayoutProps = $props();

	const footerServicesLinks = [
		{ name: "Investment Management", href: "https://fergmarcapital.com" },
		{ name: "Property Management", href: "/services/property-management" },
		{ name: "Construction & Development", href: "/services/construction-development" },
		{ name: "Sell Your Property", href: "/services/sell-your-property" }
	];

	const footerQuickLinks = [
		{ name: "About Us", href: "/company/about" },
		{ name: "Our Services", href: "/services" },
		{ name: "Properties", href: "/properties" },
		{ name: "Insights", href: "/insights" },
		{ name: "Leadership", href: "/company/leadership" },
		{ name: "Privacy Policy", href: "/privacy-policy" }
	];

	const footerResourceLinks = [
		{
			name: "Mortage Calculator",
			href: "/resources/calculators/mortgage-calculator"
		},

		{
			name: "Investment Comparison Calculator",
			href: "/resources/calculators/investment-comparison-calculator"
		},
		{ name: "Brand Guidelines", href: "https://brand.fergmar.com" }
	];

	let activeMobileItem = $state<string | null>(null);

	let showFlyout = $state(false);
	let visibleSection = $state<string | null>(null);
	let mobileMenuOpen = $state(false);
	let timeoutId = $state<ReturnType<typeof setTimeout> | null>(null);
	let isHoveringMainMenuItem = $state(false);
	let isHoveringFlyout = $state(false);

	function handleMouseEnterMainMenuItem(section: string) {
		isHoveringMainMenuItem = true;
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
		showFlyout = true;
		visibleSection = section;
	}

	function handleMouseLeaveMainMenuItem() {
		isHoveringMainMenuItem = false;
		if (!isHoveringFlyout) {
			timeoutId = setTimeout(() => {
				showFlyout = false;
				visibleSection = null;
			}, 700);
		}
	}

	function handleMouseEnterFlyout() {
		isHoveringFlyout = true;
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
	}

	function handleMouseLeaveFlyout() {
		isHoveringFlyout = false;
		if (!isHoveringMainMenuItem) {
			timeoutId = setTimeout(() => {
				showFlyout = false;
				visibleSection = null;
			}, 700);
		}
	}
</script>

<svelte:head>
	<link rel="canonical" href="{page.url.origin}{page.url.pathname}" />

	<meta property="og:url" content="{page.url.origin}{page.url.pathname}" />

	<FaviconMeta faviconSlug="fergmar" />

	{#if PUBLIC_NODE_ENV === "production"}
		<!-- Partytown config - forward necessary functions to web worker -->
		<script>
			partytown = {
				forward: ["dataLayer.push", "gtag"],
				resolveUrl: function (url, location, type) {
					if (type === "script" && !url.href.includes("/api/partytown-proxy")) {
						const proxyUrl = new URL("https://app.fergmar.com/api/partytown-proxy");
						proxyUrl.searchParams.append("url", url.href);
						return proxyUrl;
					}
					return url;
				}
			};
		</script>
		<script data-partytown={partytownSnippet()}>
			const current = document.currentScript;
			const partytown = document.createElement("script");
			partytown.textContent = current?.dataset.partytown ?? "";
			partytown.nonce = current?.nonce ?? "";
			document.head.append(partytown);
		</script>

		<!-- Google Tag Manager via Partytown -->
		<script
			type="text/partytown"
			src="https://www.googletagmanager.com/gtag/js?id=GTM-5NT2FW6K"
		></script>
		<script type="text/partytown">
			window.dataLayer = window.dataLayer || [];
			window.gtag = function () {
				dataLayer.push(arguments);
			};
			gtag("js", new Date());
			gtag("config", "GTM-5NT2FW6K");
		</script>
	{/if}
</svelte:head>

<div class="flex h-screen w-full flex-col justify-between bg-white">
	{#if PUBLIC_NODE_ENV === "production"}
		<!-- Google Tag Manager (noscript) - fallback for users without JS -->
		<noscript>
			<iframe
				src="https://www.googletagmanager.com/ns.html?id=GTM-5NT2FW6K"
				height="0"
				width="0"
				style="display:none;visibility:hidden"
				title="Google Tag Manager"
			></iframe>
		</noscript>
	{/if}
	<header
		class="fixed top-0 isolate z-50 w-full text-sm transition-colors duration-100 {showFlyout
			? 'bg-background text-foreground hover:text-foreground'
			: 'bg-foreground text-background/90 hover:text-background'}"
	>
		<hr
			class="absolute right-0 bottom-0 left-0 z-40 {showFlyout
				? 'bg-border block'
				: 'hidden'}"
		/>
		<nav class="container mx-auto flex items-center justify-between py-4" aria-label="Global">
			<div class="flex lg:flex-1">
				<a
					href="/"
					class="-m-1.5 p-1.5"
					onclick={() => {
						showFlyout = false;
						visibleSection = null;
					}}
				>
					<div class="relative h-6 w-auto">
						<span class="sr-only">Fergmar</span>
						<img
							class="h-6 w-auto"
							src={cloudinaryUrl("brand/fergmar/fergmar-logo-dark", {
								width: 300,
								trim: true
							})}
							alt="fergmar logo"
							width="300"
							height="24"
						/>
						<img
							class="absolute top-0 left-0 h-6 w-auto transition-opacity duration-300 ease-in-out"
							class:opacity-0={!showFlyout}
							class:opacity-100={showFlyout}
							src={cloudinaryUrl("brand/fergmar/fergmar-logo-light", {
								width: 300,
								trim: true
							})}
							alt="fergmar logo light"
						/>
					</div>
				</a>
			</div>

			<!-- Mobile Menu Button -->
			<div class="absolute right-6 flex lg:hidden">
				<button
					type="button"
					class="inline-flex items-center justify-center rounded"
					onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
					aria-label="Open menu"
				>
					<AlignJustifyIcon aria-hidden="true" />
				</button>
			</div>

			<!-- Desktop Nav Bar -->
			<nav aria-label="Main navigation">
				<div class="hidden lg:flex lg:gap-x-10">
					{#each data.menuItems as item (item.label)}
						{#if item.groups === undefined}
							<a
								href={item.href}
								class="font-semibold hover:scale-105"
								onclick={() => {
									showFlyout = false;
									visibleSection = null;
								}}>{item.label}</a
							>
						{:else}
							<button
								type="button"
								class="flex items-center gap-x-1 font-semibold"
								aria-expanded={visibleSection === item.label}
								aria-controls="dropdown-{item.label}"
								aria-haspopup="true"
								onmouseenter={() => handleMouseEnterMainMenuItem(item.label)}
								onmouseleave={handleMouseLeaveMainMenuItem}
							>
								<span
									class="inline-flex items-center hover:scale-105 {visibleSection ===
									item.label
										? 'underline'
										: ''}"
								>
									{item.label}
									<ChevronDownIcon
										class="ml-1 h-5 w-5 {showFlyout ? 'rotate-180' : ''}"
										aria-hidden="true"
									/>
								</span>
							</button>
						{/if}
					{/each}
					<div
						role="navigation"
						onmouseenter={handleMouseEnterFlyout}
						onmouseleave={handleMouseLeaveFlyout}
					>
						{#if showFlyout}
							<div
								id="dropdown-{visibleSection}"
								class="ring-foreground/5 absolute inset-x-0 top-0 -z-10 pt-14 shadow-lg ring-1 transition-colors duration-100 ease-in-out {showFlyout
									? 'bg-background'
									: 'bg-foreground'}"
							>
								<div class="container mx-auto flex flex-row gap-x-8 py-10">
									{#if visibleSection !== null && data.menuItems}
										{@const item = data.menuItems.find(
											(item) => item.label === visibleSection
										)}
										{#if item}
											<!-- Section Overview -->
											<div class="border-border w-1/3 border-r pr-10">
												<h2 class="text-display-lg font-medium">
													{item.label}
												</h2>
												<p class="text-foreground/70 mt-1 text-lg">
													{item.description}
												</p>
											</div>

											<!-- Columns -->
											{#if item.groups}
												{#each item.groups as group (group)}
													<div class="w-[22%]">
														<h3
															class="text-lg font-medium"
															id="group-{group.groupLabel}"
														>
															{group.groupLabel}
														</h3>

														<!-- Add group description if it exists -->
														{#if group.description}
															<p
																class="text-foreground/60 mt-1 h-20 text-sm"
															>
																{group.description}
															</p>
														{/if}

														<ul
															aria-labelledby="group-{group.groupLabel}"
															class="text-foreground mt-4 space-y-3"
														>
															<!-- Links -->
															{#each group.links as link (link)}
																<li
																	class="hover:text-primary origin-left hover:scale-105"
																>
																	<a
																		href={link.href}
																		onclick={() => {
																			showFlyout = false;
																			visibleSection = null;
																		}}>{link.label}</a
																	>
																</li>
															{/each}
														</ul>
													</div>
												{/each}
											{/if}
										{/if}
									{/if}
								</div>
							</div>
						{/if}
					</div>
				</div>
			</nav>
		</nav>
	</header>

	<!-- Mobile menu, show/hide based on menu open state. -->
	{#if mobileMenuOpen}
		<div class="lg:hidden" role="navigation" aria-label="Mobile navigation">
			<div class="fixed inset-0 z-60">
				<div
					class="bg-background sm:ring-border fixed inset-y-0 right-0 z-60 container w-full overflow-y-auto pt-6 sm:max-w-sm sm:ring-1"
				>
					<div class="flex h-[24px] w-full items-center justify-between">
						<a href="/" class="-m-1.5 p-1.5">
							<span class="sr-only">Fergmar</span>
							<img
								class="w-[150px]"
								src={mobileMenuOpen
									? cloudinaryUrl("brand/fergmar/fergmar-logo-light", {
											width: 300
										})
									: cloudinaryUrl("brand/fergmar/fergmar-logo-dark", {
											width: 300,
											trim: true
										})}
								alt="fergmar logo"
								width="300"
								height="24"
							/>
						</a>
						<Button
							variant="outline"
							class="text-foreground/70 -m-2.5 rounded p-2.5"
							onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
							aria-label="Close menu"
						>
							<span class="sr-only">Close menu</span>
							<AlignJustifyIcon aria-hidden="true" />
						</Button>
					</div>
					{#if mobileMenuOpen}
						<div
							class="bg-background text-foreground flex h-full flex-col justify-between"
						>
							<div class="space-y-2 py-6">
								<div>
									{#each data.menuItems as item (item.label)}
										<div class="border-b first:border-t last:border-b-0">
											{#if !item.groups}
												<!-- Direct Links -->
												<div class="flex items-center justify-between py-3">
													<a
														href={item.href}
														class="text-lg font-semibold"
													>
														{item.label}
													</a>
													<ExternalLinkIcon
														class="text-foreground/60 h-4 w-4"
														aria-hidden="true"
													/>
												</div>
											{:else}
												<!-- Dropdown Menu -->
												<div class="py-2">
													<button
														type="button"
														class="flex w-full items-center justify-between py-2 text-lg font-semibold"
														onclick={() => {
															activeMobileItem =
																activeMobileItem === item.label
																	? null
																	: item.label;
														}}
														aria-expanded={activeMobileItem ===
															item.label}
														aria-controls="mobile-dropdown-{item.label}"
													>
														{item.label}
														<ChevronDownIcon
															class="ml-1 h-5 w-5 transition-transform duration-200 {activeMobileItem ===
															item.label
																? 'rotate-180'
																: ''}"
															aria-hidden="true"
														/>
													</button>

													<!-- Mobile Menu Section - Updated portion -->
													{#if activeMobileItem === item.label}
														<div
															id="mobile-dropdown-{item.label}"
															class="mt-2 px-4 pb-4"
														>
															<p
																class="text-muted-foreground text-base"
															>
																{item.description}
															</p>

															{#each item.groups as group (group)}
																<div class="mt-6">
																	<h3
																		id="mobile-group-{group.groupLabel}"
																		class="text-foreground text-lg font-medium"
																	>
																		{group.groupLabel}
																	</h3>

																	<!-- Add group description for mobile -->
																	{#if group.description}
																		<p
																			class="text-foreground/60 mt-1 h-20 text-sm"
																		>
																			{group.description}
																		</p>
																	{/if}

																	<ul
																		aria-labelledby="mobile-group-{group.groupLabel}"
																		class="mt-3 space-y-3"
																	>
																		{#each group.links as link (link)}
																			<li
																				class="text-foreground/80 my-2 border-l-2 pl-4 text-base"
																			>
																				<a
																					href={link.href}
																					onclick={() => {
																						mobileMenuOpen = false;
																						activeMobileItem =
																							null;
																						visibleSection =
																							null;
																					}}
																				>
																					{link.label}
																				</a>
																			</li>
																		{/each}
																	</ul>
																</div>
															{/each}
														</div>
													{/if}
												</div>
											{/if}
										</div>
									{/each}
								</div>
							</div>
						</div>
					{/if}
				</div>
			</div>
		</div>
	{/if}

	<div class="relative grow pt-14">
		<Toaster />
		{@render children?.()}
	</div>

	<FooterWrapper entityName="Fergmar Enterprises, LLC" disclaimer>
		<Footer
			logoUrl={cloudinaryUrl("brand/fergmar/fergmar-logo-dark", { width: 300, trim: true })}
			tagline="Enhancing Communities through Real Estate"
			contactInfo={data.contactInfo}
		>
			<FooterColumn columnTitle="Services">
				{#each footerServicesLinks as link (link.name)}
					<FooterLink link={link.href} text={link.name} />
				{/each}
			</FooterColumn>

			<FooterColumn columnTitle="Quick Links">
				{#each footerQuickLinks as link (link.name)}
					<FooterLink link={link.href} text={link.name} />
				{/each}
			</FooterColumn>

			<FooterColumn columnTitle="Resources">
				{#each footerResourceLinks as link (link.name)}
					<FooterLink link={link.href} text={link.name} />
				{/each}
			</FooterColumn>
		</Footer>
	</FooterWrapper>

	<PodcastPlayer />
</div>

{#if PUBLIC_NODE_ENV === "production" && browser}
	<script>
		// Load CallRail only after first user interaction to keep it off the critical path
		function loadCallRail() {
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.async = true;
			script.src = "//cdn.callrail.com/companies/799818682/e7028b80e5341683a111/12/swap.js";
			document.head.appendChild(script);
		}

		let callRailLoaded = false;
		function onInteraction() {
			if (callRailLoaded) return;
			callRailLoaded = true;
			window.removeEventListener("scroll", onInteraction);
			window.removeEventListener("click", onInteraction);
			window.removeEventListener("touchstart", onInteraction);
			loadCallRail();
		}
		window.addEventListener("scroll", onInteraction, { once: true, passive: true });
		window.addEventListener("click", onInteraction, { once: true });
		window.addEventListener("touchstart", onInteraction, { once: true, passive: true });
	</script>
{/if}
